import { Select } from "@msuite/ui/form";
import { guardString } from "@msuite/utils/strings";
import type { Control, FieldValues, Path } from "react-hook-form";
import { UserCard } from "@/components/cards";
import { useMembers } from "@/hooks/use-members";

/** Props Interface */
interface ManagerPickerProps<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	label: string;
	isRequired: boolean;
}

/**
 * One of the organisation's project managers, as the project form picks them — the same
 * `manage-project` list and the same `UserCard` in the dropdown.
 *
 * It names a **person**, where `ProjectMemberPicker` names a membership: a project role is written
 * into `project_members` and keyed on the membership row, while everything that reasons about a
 * manager's time — a slot, an absence, an assignment — is keyed on `users.id`.
 */
export function ManagerPicker<T extends FieldValues>({
	control,
	name,
	label,
	isRequired,
}: ManagerPickerProps<T>) {
	/** Query */
	const { members } = useMembers({ capabilities: ["manage-project"] });

	/** Render */
	return (
		<Select
			label={label}
			control={control}
			name={name}
			isRequired={isRequired}
			isNullable={false}
			options={members.map((member) => ({
				label: guardString(`${member.user.firstName} ${member.user.lastName}`.trim()),
				value: member.user.id,
				item: member,
			}))}
			itemNotFoundLabel=""
			renderOption={(item) => (
				<UserCard
					variant="plain"
					userId={item.user.id}
					withPopover={false}
				/>
			)}
			data-cy="manager-picker"
		/>
	);
}
